/**
 * VendasReportPage — relatório PDF de vendas (módulo Relatórios).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Download,
  Search,
  Calendar as CalendarIcon,
  UserRound,
  Users,
  X,
  CheckCircle,
  Loader2,
  ChevronDown,
  ShoppingCart,
} from 'lucide-react'
import { CalendarRangePicker } from '../../../../components/ERP/Calendar/Calendar'
import VendasReportPdfCanvas, {
  VENDAS_COLUMN_DEFS,
  DEFAULT_VENDAS_COLUMNS,
} from '../../../../components/ERP/Relatorios/VendasReportPdfCanvas'
import '../../../../components/ERP/Financeiro/AccountsReceivablePdfCanvas.css'
import { exportElementToPdf, exportElementsToPdf } from '../../../../utils/exportElementToPdf'
import { saleService, customerService, sellerService } from '../../../../services/erpApi'
import Toast from '../../../../components/Toast/Toast'
import { useToast } from '../../../../hooks/useToast'
import { usePermissions, ACTIONS } from '../../../../hooks/usePermissions'
import { canAccessErpReport } from '../../../../utils/permissions'
import '../ContasReceber/ContasReceberReportPage.css'

const STATUS_OPTIONS = [
  { value: 'completed', label: 'Concluída' },
  { value: 'confirmed', label: 'Confirmada' },
  { value: 'draft', label: 'Rascunho' },
  { value: 'cancelled', label: 'Cancelada' },
]

const PDF_CHUNK_SIZE = 80

function toISODate(date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function daysAgoISO(days) {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  d.setDate(d.getDate() - days)
  return toISODate(d)
}

function todayISO() {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return toISODate(d)
}

function chunkArray(items, size) {
  const chunks = []
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size))
  }
  return chunks.length ? chunks : [[]]
}

function VendasReportPage() {
  const navigate = useNavigate()
  const { hasPermission } = usePermissions()
  const canView = canAccessErpReport(hasPermission, ACTIONS.REPORTS_SALES)
  const { toast, showToast, hideToast } = useToast()

  const pdfRef = useRef(null)
  const chunkRefs = useRef([])
  const periodBtnRef = useRef(null)

  const [isPeriodOpen, setIsPeriodOpen] = useState(false)
  const [generatingPDF, setGeneratingPDF] = useState(false)
  const [exportSales, setExportSales] = useState([])
  const [exportChunks, setExportChunks] = useState([])
  const [exportTotalsSales, setExportTotalsSales] = useState([])
  const [previewCount, setPreviewCount] = useState(null)
  const [previewLoading, setPreviewLoading] = useState(false)

  const [selectedCustomers, setSelectedCustomers] = useState([])
  const [customerModalOpen, setCustomerModalOpen] = useState(false)
  const [customerSearchTerm, setCustomerSearchTerm] = useState('')
  const [modalCustomers, setModalCustomers] = useState([])
  const [loadingCustomers, setLoadingCustomers] = useState(false)
  const [draftCustomers, setDraftCustomers] = useState([])

  const [selectedSellers, setSelectedSellers] = useState([])
  const [sellerModalOpen, setSellerModalOpen] = useState(false)
  const [sellerSearchTerm, setSellerSearchTerm] = useState('')
  const [modalSellers, setModalSellers] = useState([])
  const [loadingSellers, setLoadingSellers] = useState(false)
  const [draftSellers, setDraftSellers] = useState([])

  const [visibleColumns, setVisibleColumns] = useState(() => [...DEFAULT_VENDAS_COLUMNS])

  const [filters, setFilters] = useState({
    search: '',
    status: ['completed', 'confirmed'],
    dateFrom: daysAgoISO(30),
    dateTo: todayISO(),
  })

  const customerLabel = useMemo(() => {
    if (!selectedCustomers.length) return 'Todos os clientes'
    if (selectedCustomers.length === 1) return selectedCustomers[0].name
    return `${selectedCustomers.length} clientes selecionados`
  }, [selectedCustomers])

  const sellerLabel = useMemo(() => {
    if (!selectedSellers.length) return 'Todos os vendedores'
    if (selectedSellers.length === 1) return selectedSellers[0].name
    return `${selectedSellers.length} vendedores selecionados`
  }, [selectedSellers])

  const periodLabel = useMemo(() => {
    if (filters.dateFrom && filters.dateTo) {
      return `${filters.dateFrom} → ${filters.dateTo}`
    }
    if (filters.dateFrom) return `A partir de ${filters.dateFrom}`
    if (filters.dateTo) return `Até ${filters.dateTo}`
    return 'Período'
  }, [filters.dateFrom, filters.dateTo])

  const pdfFilters = useMemo(
    () => ({
      search: filters.search,
      status: filters.status,
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
      selectedCustomerNames: selectedCustomers.map((c) => c.name),
      selectedSellerNames: selectedSellers.map((s) => s.name),
    }),
    [filters, selectedCustomers, selectedSellers]
  )

  useEffect(() => {
    if (!customerModalOpen) return
    const load = async () => {
      setLoadingCustomers(true)
      try {
        const params = customerSearchTerm.trim()
          ? { search: customerSearchTerm.trim(), status: 'active', limit: 100 }
          : { status: 'active', limit: 1000 }
        const response = await customerService.getAll(params)
        setModalCustomers(response.success ? response.data || [] : [])
      } catch (error) {
        console.error('Erro ao buscar clientes:', error)
        setModalCustomers([])
      } finally {
        setLoadingCustomers(false)
      }
    }
    const t = setTimeout(load, customerSearchTerm.trim() ? 300 : 0)
    return () => clearTimeout(t)
  }, [customerModalOpen, customerSearchTerm])

  useEffect(() => {
    if (!sellerModalOpen) return
    const load = async () => {
      setLoadingSellers(true)
      try {
        const params = sellerSearchTerm.trim()
          ? { search: sellerSearchTerm.trim() }
          : {}
        const response = await sellerService.getAll(params)
        setModalSellers(response.success ? response.data || [] : [])
      } catch (error) {
        console.error('Erro ao buscar vendedores:', error)
        setModalSellers([])
      } finally {
        setLoadingSellers(false)
      }
    }
    const t = setTimeout(load, sellerSearchTerm.trim() ? 300 : 0)
    return () => clearTimeout(t)
  }, [sellerModalOpen, sellerSearchTerm])

  useEffect(() => {
    if (!customerModalOpen && !sellerModalOpen) return undefined
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setCustomerModalOpen(false)
        setSellerModalOpen(false)
        setCustomerSearchTerm('')
        setSellerSearchTerm('')
      }
    }
    document.addEventListener('keydown', handleEscape)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [customerModalOpen, sellerModalOpen])

  const toggleStatus = (statusValue) => {
    setFilters((prev) => {
      const current = Array.isArray(prev.status) ? prev.status : []
      const selected = current.includes(statusValue)
        ? current.filter((s) => s !== statusValue)
        : [...current, statusValue]
      return {
        ...prev,
        status: selected.length ? selected : ['completed'],
      }
    })
    setPreviewCount(null)
  }

  const applyCustomerSelection = useCallback((customers) => {
    setSelectedCustomers(customers)
    setPreviewCount(null)
  }, [])

  const applySellerSelection = useCallback((sellers) => {
    setSelectedSellers(sellers)
    setPreviewCount(null)
  }, [])

  const toggleDraftCustomer = (customer) => {
    const exists = draftCustomers.some((c) => String(c.id) === String(customer.id))
    const next = exists
      ? draftCustomers.filter((c) => String(c.id) !== String(customer.id))
      : [...draftCustomers, { id: customer.id, name: customer.name }]
    setDraftCustomers(next)
    applyCustomerSelection(next)
  }

  const toggleDraftSeller = (seller) => {
    const exists = draftSellers.some((s) => String(s.id) === String(seller.id))
    const next = exists
      ? draftSellers.filter((s) => String(s.id) !== String(seller.id))
      : [...draftSellers, { id: seller.id, name: seller.name }]
    setDraftSellers(next)
    applySellerSelection(next)
  }

  const toggleColumn = (columnId) => {
    setVisibleColumns((prev) => {
      if (prev.includes(columnId)) {
        if (prev.length === 1) return prev
        return prev.filter((id) => id !== columnId)
      }
      return [...prev, columnId]
    })
  }

  const buildQuery = useCallback(() => {
    const params = {
      type: 'sale',
      page: 1,
      limit: 10000,
      search: filters.search.trim() || undefined,
      dateFrom: filters.dateFrom || undefined,
      dateTo: filters.dateTo || undefined,
    }

    if (Array.isArray(filters.status) && filters.status.length) {
      params.status = filters.status.join(',')
    }

    if (selectedCustomers.length === 1) {
      params.customerId = selectedCustomers[0].id
    } else if (selectedCustomers.length > 1) {
      params.customerIds = selectedCustomers.map((c) => c.id).join(',')
    }

    if (selectedSellers.length === 1) {
      params.sellerId = selectedSellers[0].id
    } else if (selectedSellers.length > 1) {
      params.sellerIds = selectedSellers.map((s) => s.id).join(',')
    }

    Object.keys(params).forEach((key) => {
      if (params[key] === undefined || params[key] === '') delete params[key]
    })

    return params
  }, [filters, selectedCustomers, selectedSellers])

  const fetchSales = useCallback(async () => {
    const response = await saleService.getAll(buildQuery())
    if (!response.success) {
      throw new Error(response.message || 'Erro ao consultar vendas')
    }
    return Array.isArray(response.data) ? response.data : []
  }, [buildQuery])

  const handlePreviewCount = async () => {
    setPreviewLoading(true)
    try {
      const sales = await fetchSales()
      setPreviewCount(sales.length)
    } catch (err) {
      console.error(err)
      showToast(err.message || 'Erro ao consultar vendas', 'error')
    } finally {
      setPreviewLoading(false)
    }
  }

  const handleGeneratePdf = async () => {
    setGeneratingPDF(true)
    try {
      const sales = await fetchSales()

      if (!sales.length) {
        showToast('Nenhuma venda encontrada com os filtros atuais', 'warning')
        return
      }

      if (!visibleColumns.length) {
        showToast('Selecione ao menos uma coluna para o PDF', 'warning')
        return
      }

      setPreviewCount(sales.length)
      const chunks = chunkArray(sales, PDF_CHUNK_SIZE)
      const filename = `Relatorio_Vendas_${Date.now()}.pdf`
      const scale = sales.length > 120 ? 1.5 : 2

      if (chunks.length === 1) {
        setExportSales(sales)
        setExportChunks([])
        setExportTotalsSales([])
        await new Promise((resolve) => setTimeout(resolve, 250))
        if (!pdfRef.current) throw new Error('Falha ao montar documento PDF')
        await exportElementToPdf(pdfRef.current, filename, { scale })
      } else {
        setExportSales([])
        setExportTotalsSales(sales)
        setExportChunks(chunks)
        chunkRefs.current = []
        await new Promise((resolve) => setTimeout(resolve, 350))
        const elements = chunkRefs.current.filter(Boolean)
        if (elements.length !== chunks.length) {
          throw new Error('Falha ao montar documento PDF em lotes')
        }
        await exportElementsToPdf(elements, filename, { scale })
      }

      showToast(`${sales.length} venda(s) exportada(s) em PDF`, 'success')
    } catch (error) {
      console.error('Erro ao exportar PDF de vendas:', error)
      showToast(error.message || 'Erro ao gerar PDF. Tente novamente.', 'error')
    } finally {
      setGeneratingPDF(false)
      setExportSales([])
      setExportChunks([])
      setExportTotalsSales([])
      chunkRefs.current = []
    }
  }

  if (!canView) {
    return (
      <div className="cr-report-page">
        <p className="cr-report-denied">Você não tem permissão para este relatório.</p>
      </div>
    )
  }

  return (
    <div className="cr-report-page">
      <div className="cr-report-header">
        <div className="cr-report-header-left">
          <button
            type="button"
            className="cr-report-back"
            onClick={() => navigate('/erp/relatorios')}
            aria-label="Voltar"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="cr-report-title">Vendas</h1>
            <p className="cr-report-subtitle">Relação de vendas por período — gere o PDF</p>
          </div>
        </div>
        <button
          type="button"
          className="cr-report-btn-primary"
          onClick={handleGeneratePdf}
          disabled={generatingPDF}
        >
          <Download size={16} />
          {generatingPDF ? 'Gerando…' : 'Gerar PDF'}
        </button>
      </div>

      <div className="cr-report-card">
        <h2 className="cr-report-section-title">Filtros</h2>

        <div className="cr-report-field">
          <label htmlFor="vd-search">Busca</label>
          <div className="cr-report-input-wrap">
            <Search size={16} className="cr-report-input-icon" />
            <input
              id="vd-search"
              type="text"
              placeholder="Número da venda ou cliente…"
              value={filters.search}
              onChange={(e) => {
                setFilters((prev) => ({ ...prev, search: e.target.value }))
                setPreviewCount(null)
              }}
            />
          </div>
        </div>

        <div className="cr-report-field">
          <span className="cr-report-label">Período da venda</span>
          <div className="cr-report-period-row">
            <button
              type="button"
              ref={periodBtnRef}
              className="cr-report-period-btn"
              onClick={() => setIsPeriodOpen((v) => !v)}
            >
              <CalendarIcon size={16} />
              {periodLabel}
            </button>
            <div className="cr-report-presets">
              {[7, 15, 30, 90].map((days) => (
                <button
                  key={days}
                  type="button"
                  className={`cr-report-chip${
                    filters.dateFrom === daysAgoISO(days) && filters.dateTo === todayISO()
                      ? ' is-active'
                      : ''
                  }`}
                  onClick={() => {
                    setFilters((prev) => ({
                      ...prev,
                      dateFrom: daysAgoISO(days),
                      dateTo: todayISO(),
                    }))
                    setPreviewCount(null)
                  }}
                >
                  {days}d
                </button>
              ))}
            </div>
          </div>
          <CalendarRangePicker
            open={isPeriodOpen}
            onOpenChange={setIsPeriodOpen}
            anchorRef={periodBtnRef}
            startValue={filters.dateFrom}
            endValue={filters.dateTo}
            onRangeChange={(start, end) => {
              let from = start
              let to = end
              if (from && to && from > to) {
                ;[from, to] = [to, from]
              }
              setFilters((prev) => ({
                ...prev,
                dateFrom: from || '',
                dateTo: to || '',
              }))
              setPreviewCount(null)
            }}
            onClear={() => {
              setFilters((prev) => ({
                ...prev,
                dateFrom: daysAgoISO(30),
                dateTo: todayISO(),
              }))
              setPreviewCount(null)
            }}
            title="Período"
            subtitle="Início e Fim"
          />
        </div>

        <div className="cr-report-field">
          <span className="cr-report-label">Status</span>
          <div className="cr-report-chips">
            {STATUS_OPTIONS.map((opt) => {
              const active = filters.status.includes(opt.value)
              return (
                <button
                  key={opt.value}
                  type="button"
                  className={`cr-report-chip${active ? ' is-active' : ''}`}
                  onClick={() => toggleStatus(opt.value)}
                >
                  {opt.label}
                </button>
              )
            })}
          </div>
        </div>

        <div className="cr-report-field">
          <span className="cr-report-label">Cliente</span>
          <button
            type="button"
            className={`cr-report-customer-trigger${selectedCustomers.length ? ' has-value' : ''}`}
            onClick={() => {
              setDraftCustomers(selectedCustomers)
              setCustomerSearchTerm('')
              setCustomerModalOpen(true)
            }}
          >
            <UserRound size={16} className="cr-report-customer-trigger-icon" />
            <span className="cr-report-customer-trigger-text">{customerLabel}</span>
            <ChevronDown size={16} />
          </button>
        </div>

        <div className="cr-report-field">
          <span className="cr-report-label">Vendedor</span>
          <button
            type="button"
            className={`cr-report-customer-trigger${selectedSellers.length ? ' has-value' : ''}`}
            onClick={() => {
              setDraftSellers(selectedSellers)
              setSellerSearchTerm('')
              setSellerModalOpen(true)
            }}
          >
            <Users size={16} className="cr-report-customer-trigger-icon" />
            <span className="cr-report-customer-trigger-text">{sellerLabel}</span>
            <ChevronDown size={16} />
          </button>
        </div>

        <div className="cr-report-field">
          <div className="cr-report-columns-header">
            <span className="cr-report-label">Colunas do PDF</span>
            <div className="cr-report-columns-actions">
              <button
                type="button"
                className="cr-report-link-btn"
                onClick={() => setVisibleColumns(VENDAS_COLUMN_DEFS.map((c) => c.id))}
              >
                Todas
              </button>
              <button
                type="button"
                className="cr-report-link-btn"
                onClick={() => setVisibleColumns([...DEFAULT_VENDAS_COLUMNS])}
              >
                Padrão
              </button>
            </div>
          </div>
          <div className="cr-report-columns-grid">
            {VENDAS_COLUMN_DEFS.map((col) => {
              const checked = visibleColumns.includes(col.id)
              return (
                <label key={col.id} className={`cr-report-column-check${checked ? ' is-on' : ''}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleColumn(col.id)}
                  />
                  <span>{col.label}</span>
                </label>
              )
            })}
          </div>
        </div>

        <div className="cr-report-actions">
          <button
            type="button"
            className="cr-report-btn-secondary"
            onClick={handlePreviewCount}
            disabled={previewLoading || generatingPDF}
          >
            {previewLoading ? 'Consultando…' : 'Contar vendas'}
          </button>
          {previewCount != null && (
            <span className="cr-report-count">
              {previewCount} venda{previewCount === 1 ? '' : 's'} com os filtros atuais
            </span>
          )}
        </div>
      </div>

      {customerModalOpen && (
        <div
          className="cr-report-modal-overlay"
          onClick={() => {
            setCustomerModalOpen(false)
            setCustomerSearchTerm('')
          }}
        >
          <div
            className="cr-report-modal-content"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="cr-report-modal-header">
              <div className="cr-report-modal-header-left">
                <div className="cr-report-modal-icon">
                  <UserRound size={24} />
                </div>
                <div>
                  <h3 className="cr-report-modal-title">Selecionar Cliente</h3>
                  <p className="cr-report-modal-subtitle">Um, vários ou todos</p>
                </div>
              </div>
              <button
                type="button"
                className="cr-report-modal-close"
                onClick={() => {
                  setCustomerModalOpen(false)
                  setCustomerSearchTerm('')
                }}
              >
                <X size={24} />
              </button>
            </div>
            <div className="cr-report-modal-body">
              <div className="cr-report-search-box">
                <Search size={18} className="cr-report-search-icon" />
                <input
                  type="text"
                  className="cr-report-search-input"
                  placeholder="Buscar cliente..."
                  value={customerSearchTerm}
                  onChange={(e) => setCustomerSearchTerm(e.target.value)}
                  autoFocus
                />
                {loadingCustomers && (
                  <Loader2 size={16} className="cr-report-spinning" />
                )}
              </div>
              <button
                type="button"
                className={`cr-report-select-item cr-report-select-all${draftCustomers.length === 0 ? ' selected' : ''}`}
                onClick={() => {
                  setDraftCustomers([])
                  applyCustomerSelection([])
                }}
              >
                <div className="cr-report-select-item-avatar">
                  <UserRound size={20} />
                </div>
                <div className="cr-report-select-item-info">
                  <div className="cr-report-select-item-name">Todos os clientes</div>
                </div>
                {draftCustomers.length === 0 && (
                  <CheckCircle size={20} className="cr-report-select-item-check" />
                )}
              </button>
              <div className="cr-report-select-list">
                {loadingCustomers ? (
                  <div className="cr-report-select-empty">
                    <Loader2 size={32} className="cr-report-spinning" />
                    <p>Buscando...</p>
                  </div>
                ) : (
                  modalCustomers.map((customer) => {
                    const selected = draftCustomers.some(
                      (c) => String(c.id) === String(customer.id)
                    )
                    return (
                      <button
                        type="button"
                        key={customer.id}
                        className={`cr-report-select-item${selected ? ' selected' : ''}`}
                        onClick={() => toggleDraftCustomer(customer)}
                      >
                        <div className="cr-report-select-item-avatar">
                          <UserRound size={20} />
                        </div>
                        <div className="cr-report-select-item-info">
                          <div className="cr-report-select-item-name">{customer.name}</div>
                        </div>
                        {selected && (
                          <CheckCircle size={20} className="cr-report-select-item-check" />
                        )}
                      </button>
                    )
                  })
                )}
              </div>
            </div>
            <div className="cr-report-modal-footer">
              <button
                type="button"
                className="cr-report-modal-btn-secondary"
                onClick={() => {
                  setCustomerModalOpen(false)
                  setCustomerSearchTerm('')
                }}
              >
                Fechar
              </button>
              <button
                type="button"
                className="cr-report-modal-btn-primary"
                onClick={() => {
                  applyCustomerSelection(draftCustomers)
                  setCustomerModalOpen(false)
                  setCustomerSearchTerm('')
                }}
              >
                {draftCustomers.length === 0
                  ? 'Usar todos'
                  : `Confirmar (${draftCustomers.length})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {sellerModalOpen && (
        <div
          className="cr-report-modal-overlay"
          onClick={() => {
            setSellerModalOpen(false)
            setSellerSearchTerm('')
          }}
        >
          <div
            className="cr-report-modal-content"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="cr-report-modal-header">
              <div className="cr-report-modal-header-left">
                <div className="cr-report-modal-icon">
                  <Users size={24} />
                </div>
                <div>
                  <h3 className="cr-report-modal-title">Selecionar Vendedor</h3>
                  <p className="cr-report-modal-subtitle">Um, vários ou todos</p>
                </div>
              </div>
              <button
                type="button"
                className="cr-report-modal-close"
                onClick={() => {
                  setSellerModalOpen(false)
                  setSellerSearchTerm('')
                }}
              >
                <X size={24} />
              </button>
            </div>
            <div className="cr-report-modal-body">
              <div className="cr-report-search-box">
                <Search size={18} className="cr-report-search-icon" />
                <input
                  type="text"
                  className="cr-report-search-input"
                  placeholder="Buscar vendedor..."
                  value={sellerSearchTerm}
                  onChange={(e) => setSellerSearchTerm(e.target.value)}
                  autoFocus
                />
                {loadingSellers && (
                  <Loader2 size={16} className="cr-report-spinning" />
                )}
              </div>
              <button
                type="button"
                className={`cr-report-select-item cr-report-select-all${draftSellers.length === 0 ? ' selected' : ''}`}
                onClick={() => {
                  setDraftSellers([])
                  applySellerSelection([])
                }}
              >
                <div className="cr-report-select-item-avatar">
                  <Users size={20} />
                </div>
                <div className="cr-report-select-item-info">
                  <div className="cr-report-select-item-name">Todos os vendedores</div>
                </div>
                {draftSellers.length === 0 && (
                  <CheckCircle size={20} className="cr-report-select-item-check" />
                )}
              </button>
              <div className="cr-report-select-list">
                {loadingSellers ? (
                  <div className="cr-report-select-empty">
                    <Loader2 size={32} className="cr-report-spinning" />
                    <p>Buscando...</p>
                  </div>
                ) : (
                  modalSellers.map((seller) => {
                    const selected = draftSellers.some(
                      (s) => String(s.id) === String(seller.id)
                    )
                    return (
                      <button
                        type="button"
                        key={seller.id}
                        className={`cr-report-select-item${selected ? ' selected' : ''}`}
                        onClick={() => toggleDraftSeller(seller)}
                      >
                        <div className="cr-report-select-item-avatar">
                          <Users size={20} />
                        </div>
                        <div className="cr-report-select-item-info">
                          <div className="cr-report-select-item-name">{seller.name}</div>
                        </div>
                        {selected && (
                          <CheckCircle size={20} className="cr-report-select-item-check" />
                        )}
                      </button>
                    )
                  })
                )}
              </div>
            </div>
            <div className="cr-report-modal-footer">
              <button
                type="button"
                className="cr-report-modal-btn-secondary"
                onClick={() => {
                  setSellerModalOpen(false)
                  setSellerSearchTerm('')
                }}
              >
                Fechar
              </button>
              <button
                type="button"
                className="cr-report-modal-btn-primary"
                onClick={() => {
                  applySellerSelection(draftSellers)
                  setSellerModalOpen(false)
                  setSellerSearchTerm('')
                }}
              >
                {draftSellers.length === 0
                  ? 'Usar todos'
                  : `Confirmar (${draftSellers.length})`}
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast show={toast.show} message={toast.message} type={toast.type} onClose={hideToast} />

      {generatingPDF && (
        <div className="ar-pdf-overlay">
          <div className="ar-pdf-overlay-card">
            <ShoppingCart size={28} color="#10b981" />
            <h4>Gerando PDF</h4>
            <p>Preparando relatório de vendas...</p>
          </div>
        </div>
      )}

      {exportSales.length > 0 && (
        <div className="ar-pdf-offscreen" aria-hidden>
          <VendasReportPdfCanvas
            ref={pdfRef}
            sales={exportSales}
            filters={pdfFilters}
            columns={visibleColumns}
          />
        </div>
      )}

      {exportChunks.length > 0 && (
        <div className="ar-pdf-offscreen" aria-hidden>
          {exportChunks.map((chunk, index) => (
            <VendasReportPdfCanvas
              key={`chunk-${index}`}
              ref={(el) => {
                chunkRefs.current[index] = el
              }}
              sales={chunk}
              filters={pdfFilters}
              columns={visibleColumns}
              chunkMeta={{
                index,
                total: exportChunks.length,
                totalsSales: exportTotalsSales,
              }}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default VendasReportPage
